/**
 * What a civilisation knows — the part of research everything else depends on.
 *
 * Split from index.ts so that production, work and the rest can ask "is this unlocked?"
 * without importing the research turn, which depends on the economy, which depends on
 * production: a cycle that works by accident in ES modules until the day it does not.
 */

import type { Content } from '../../../content/schema/index.js';
import type { Civ } from '../state/types.js';

export function knows(civ: Civ, techId: number): boolean {
  return civ.techs.includes(techId);
}

/** Whether a requirement is met. A requirement of -1 means none. */
export function hasTech(civ: Civ, techId: number): boolean {
  return techId < 0 || knows(civ, techId);
}

export interface Unlock {
  kind: 'unit' | 'building' | 'improvement' | 'government' | 'resource';
  id: number;
  name: string;
}

const unlockCache = new WeakMap<Content, Unlock[][]>();

/**
 * Everything each technology opens up, indexed by technology id.
 *
 * Derived from the requirement on each unit, building and improvement rather than
 * listed on the technology, so adding a unit never means editing the tree — and so the
 * two can never disagree.
 */
export function unlocksOf(content: Content, techId: number): readonly Unlock[] {
  let table = unlockCache.get(content);
  if (!table) {
    table = content.technologies.all.map(() => []);
    // Unique units stand in for a standard one already listed (ADR-0062).
    for (const u of content.units.all) if (u.techId >= 0 && u.civId === -1) table[u.techId]!.push({ kind: 'unit', id: u.id, name: u.name });
    for (const b of content.buildings.all) if (b.techId >= 0) table[b.techId]!.push({ kind: 'building', id: b.id, name: b.name });
    for (const i of content.improvements.all) if (i.techId >= 0) table[i.techId]!.push({ kind: 'improvement', id: i.id, name: i.name });
    for (const g of content.governments.all) if (g.techId >= 0) table[g.techId]!.push({ kind: 'government', id: g.id, name: g.name });
    // A strategic resource it reveals on the map (ADR-0063).
    for (const r of content.resources.all) if (r.revealedId >= 0) table[r.revealedId]!.push({ kind: 'resource', id: r.id, name: r.name });
    unlockCache.set(content, table);
  }
  return table[techId] ?? [];
}
